import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Section, { SectionTitle } from '../../common/Section.js';
import Button from '../../common/Button.js';
import { FaQuoteLeft } from 'react-icons/fa';

const TestimonialsWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  
  .slick-slide {
    padding: 0 15px;
  }
  
  .slick-dots li button:before {
    font-size: 12px;
    color: var(--secondary);
  }
  
  .slick-dots li.slick-active button:before {
    color: var(--secondary);
  }
`;

const TestimonialCard = styled.div`
  background-color: var(--white);
  padding: 2.5rem;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-md);
  margin-bottom: 2rem;
  
  .quote-icon {
    color: var(--secondary);
    font-size: 2rem;
    margin-bottom: 1.5rem;
    opacity: 0.5;
  }
  
  p {
    font-style: italic;
    margin-bottom: 1.5rem;
    line-height: 1.8;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  
  img {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 1rem;
  }
  
  .info {
    .name {
      font-weight: 700;
      font-size: 1.125rem;
      margin-bottom: 0.25rem;
    }
    
    .role {
      color: var(--gray);
      font-size: 0.875rem;
    }
  }
`;

const Testimonials = () => {
  const testimonials = [
    // Original testimonials
    {
      id: 1,
      text: "We couldn't be happier with our new home from Shoffner Homes! The quality of construction and attention to detail is outstanding. The team was professional and kept us informed throughout the entire process.",
      name: "Sarah Johnson",
      role: "Homeowner in Greensboro",
      image: "/images/testimonial1.png"
    },
    {
      id: 2,
      text: "Working with Shoffner Homes on our custom build was an amazing experience. They took our vision and turned it into reality, all while keeping the project on time and within budget.",
      name: "Michael Peterson",
      role: "Custom Home Client",
      image: "/images/testimonial2.png"
    },
    {
      id: 3,
      text: "As a first-time homebuyer, I was nervous about the process, but Shoffner Homes made it easy. They were patient with my questions and helped me find a home that perfectly fits my needs and budget.",
      name: "Amanda Rodriguez",
      role: "First-Time Homebuyer",
      image: "/images/testimonial3.png"
    },
    
    // Additional testimonials
    {
      id: 4,
      text: "The level of craftsmanship in our Shoffner home is exceptional. From the custom cabinetry to the trim work, everything was done with precision and care. We're proud to show off our home to family and friends.",
      name: "David & Lisa Thompson",
      role: "Homeowners in High Point",
      image: "/images/testimonial1.png"
    },
    {
      id: 5,
      text: "Moving from out of state was stressful, but Shoffner Homes made building our dream home seamless. They communicated with us remotely and delivered exactly what we wanted, even exceeding our expectations.",
      name: "Robert Chen",
      role: "Relocating Homeowner in Guilford County",
      image: "/images/testimonial2.png"
    },
    {
      id: 6,
      text: "The energy efficiency of our Shoffner home has been remarkable. Our utility bills are significantly lower than our previous home, and the comfort level is consistent throughout all rooms and seasons.",
      name: "Jennifer Williams",
      role: "Eco-Conscious Homeowner in Burlington",
      image: "/images/testimonial3.png"
    },
    {
      id: 7,
      text: "When we decided to downsize, we wanted quality over quantity. Shoffner Homes designed us a smaller home that still feels spacious and includes all the premium features we wanted. It's perfect for this stage of our lives.",
      name: "Tom & Margaret Wilson",
      role: "Retirees in Alamance County",
      image: "/images/testimonial1.png"
    },
    {
      id: 8,
      text: "As someone who works from home, I needed a dedicated office space that was functional and inspiring. Shoffner Homes created the perfect work environment that has helped my business thrive.",
      name: "Sophia Garcia",
      role: "Entrepreneur in Greensboro",
      image: "/images/testimonial2.png"
    },
    {
      id: 9,
      text: "The Shoffner team listened carefully to our needs as a growing family. They suggested smart design choices we hadn't considered that have made daily life with young children so much easier.",
      name: "Chris & Emily Davis",
      role: "Young Family in High Point",
      image: "/images/testimonial3.png"
    },
    {
      id: 10,
      text: "After living in our Shoffner home for three years, we're still impressed by how well it was built. Zero maintenance issues and the finishes look as good as the day we moved in.",
      name: "James Cooper",
      role: "Long-term Homeowner in Rockingham County",
      image: "/images/testimonial1.png"
    },
    {
      id: 11,
      text: "The outdoor living space Shoffner Homes designed for us has become our favorite part of the house. The covered patio and fireplace allow us to enjoy North Carolina's beautiful weather year-round.",
      name: "Patricia Miller",
      role: "Outdoor Enthusiast in Guilford County",
      image: "/images/testimonial2.png"
    },
    {
      id: 12,
      text: "We've built three houses previously with other builders, and our experience with Shoffner Homes was by far the most positive. Their transparency throughout the process eliminated the usual stress of building.",
      name: "Richard & Susan Taylor",
      role: "Experienced Homebuilders in Burlington",
      image: "/images/testimonial3.png"
    },
    {
      id: 13,
      text: "When unexpected challenges arose during our build, the Shoffner team handled them with calm professionalism. Their problem-solving skills and flexibility turned potential issues into opportunities for improvement.",
      name: "Linda Martinez",
      role: "Homeowner in Greensboro",
      image: "/images/testimonial1.png"
    },
    {
      id: 14,
      text: "We appreciated how Shoffner Homes incorporated universal design elements that will allow us to age in place. The thoughtful features are subtle but will make a huge difference as our mobility changes.",
      name: "Harold & June Anderson",
      role: "Forward-thinking Retirees in Alamance County",
      image: "/images/testimonial2.png"
    },
    {
      id: 15,
      text: "The open concept kitchen and living area in our Shoffner home has transformed how we entertain. We've hosted everything from holiday gatherings to dinner parties with ease and received countless compliments.",
      name: "Michelle Sanders",
      role: "Social Host in High Point",
      image: "/images/testimonial3.png"
    },
    {
      id: 16,
      text: "We were concerned about building during the pandemic, but Shoffner Homes adapted their processes to keep everyone safe while staying on schedule. That level of commitment to both safety and timelines was impressive.",
      name: "Daniel & Olivia Wright",
      role: "Recent Homeowners in Guilford County",
      image: "/images/testimonial1.png"
    },
    {
      id: 17,
      text: "The design assistance Shoffner Homes provided was invaluable. They helped us choose cohesive finishes that reflect our personal style while also ensuring long-term value and appeal.",
      name: "Kimberly Nelson",
      role: "Design-focused Client in Rockingham County",
      image: "/images/testimonial2.png"
    },
    {
      id: 18,
      text: "As a local real estate agent, I've seen the quality difference in Shoffner homes. When my clients choose Shoffner, I'm confident they're making a solid investment that will stand the test of time.",
      name: "Brandon Thomas",
      role: "Real Estate Professional in Greensboro",
      image: "/images/testimonial3.png"
    },
    {
      id: 19,
      text: "The smart home features Shoffner integrated into our house have made everyday living more convenient and efficient. From lighting to security, everything works seamlessly together.",
      name: "Ethan & Zoe Phillips",
      role: "Tech-savvy Homeowners in Burlington",
      image: "/images/testimonial1.png"
    },
    {
      id: 20,
      text: "Shoffner Homes' knowledge of local neighborhoods helped us choose the perfect location for our new home. Their insights about school districts and community development proved invaluable.",
      name: "Natalie Robinson",
      role: "New Resident in Alamance County",
      image: "/images/testimonial2.png"
    },
    {
      id: 21,
      text: "The difference is in the details, and Shoffner Homes nails every detail. From perfect crown molding to aligned electrical outlets, their commitment to excellence shows in things most builders would overlook.",
      name: "Gregory Barnes",
      role: "Detail-oriented Client in High Point",
      image: "/images/testimonial3.png"
    },
    {
      id: 22,
      text: "When we had to modify our plans mid-construction, we expected resistance. Instead, Shoffner Homes embraced the changes and worked creatively to implement them without significant delays or cost overruns.",
      name: "Rebecca & Justin Coleman",
      role: "Adaptable Homeowners in Guilford County",
      image: "/images/testimonial1.png"
    },
    {
      id: 23,
      text: "Our multigenerational family has unique needs, and Shoffner Homes designed a layout that gives everyone privacy while fostering togetherness in shared spaces. It's brought our family closer together.",
      name: "Wei & Lin Zhang",
      role: "Multigenerational Family in Rockingham County",
      image: "/images/testimonial2.png"
    },
    {
      id: 24,
      text: "The basement finish in our Shoffner home is exceptional—fully integrated with the rest of the house in terms of quality and design. It doesn't feel like an afterthought as basements often do.",
      name: "Scott & Heather Reynolds",
      role: "Entertainment Space Enthusiasts in Greensboro",
      image: "/images/testimonial3.png"
    },
    {
      id: 25,
      text: "We've received more compliments on our Shoffner home than we can count. Friends always ask who our builder was and comment on the thoughtful layout and beautiful finishes.",
      name: "Alicia Montgomery",
      role: "Proud Homeowner in Burlington",
      image: "/images/testimonial1.png"
    }
  ];
  
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true
  };
  
  return (
    <Section bg="light">
      <div className="container">
        <SectionTitle>
          <h2>What Our Clients Say</h2>
          <p>Hear from homeowners who've experienced the Shoffner Homes difference</p>
        </SectionTitle>
        
        <TestimonialsWrapper>
          {/* Display just the first testimonial for now */}
          <TestimonialCard>
            <div className="quote-icon">
              <FaQuoteLeft />
            </div>
            <p>"{testimonials[0].text}"</p>
            <TestimonialAuthor>
              <img src={testimonials[0].image} alt={testimonials[0].name} />
              <div className="info">
                <div className="name">{testimonials[0].name}</div>
                <div className="role">{testimonials[0].role}</div>
              </div>
            </TestimonialAuthor>
          </TestimonialCard>
          
          {/* Read More Button */}
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Button as={Link} to="/testimonials" variant="outline">
              Read More Client Stories
            </Button>
          </div>
        </TestimonialsWrapper>
      </div>
    </Section>
  );
};

export default Testimonials;